import { useEffect, useState } from 'react';
import { request, API_PREFIX, tokenStore } from '@/api/client';
import { useAuth } from '@/context/AuthContext';

type RecordRow = { task: number; status: string; revision: number; measured_seconds: number; needed_help: boolean };
type State = { enabled: boolean; participant?: string; tasks?: string[]; records?: RecordRow[] };
const statusLabel: Record<string, string> = { running: 'En curso', paused: 'Pausada', completed: 'Completada (declarado)', blocked: 'No completada (declarado)' };

export default function EvaluationControl() {
  const { user } = useAuth();
  const candidate = !!user && user.role === 'PSYCHOLOGIST' && /^evaluacion\.p0[1-5]\.20261104@psicosalud\.py$/.test(user.email);
  const [state, setState] = useState<State | null>(null);
  const [task, setTask] = useState(1);
  const [help, setHelp] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [received, setReceived] = useState(0);
  const [tick, setTick] = useState(0);
  const [uncertain, setUncertain] = useState(false);
  const accept = (s: State) => {
    setState(s); setReceived(performance.now()); setTick(0); setUncertain(false);
    const active = s.records?.find(r => r.status === 'running' || r.status === 'paused');
    const next = s.tasks?.findIndex((_, i) => !s.records?.some(r => r.task === i+1)) ?? -1;
    setTask(active?.task ?? (next >= 0 ? next + 1 : 1));
    setHelp(active?.needed_help ?? false);
  };
  async function reload() {
    setBusy(true); setError('');
    try { accept(await request<State>('/evaluation/me')); }
    catch { setError('No se pudo recuperar la medición. Reintentá antes de continuar la prueba.'); setUncertain(true); }
    finally { setBusy(false); }
  }
  useEffect(() => {
    let cancelled = false;
    if (!candidate) { setState(null); return; }
    setBusy(true);
    request<State>('/evaluation/me').then(s => { if (!cancelled) accept(s); })
      .catch(() => { if (!cancelled) { setError('No se pudo recuperar la medición.'); setUncertain(true); } })
      .finally(() => { if (!cancelled) setBusy(false); });
    return () => { cancelled = true; };
  }, [candidate, user?.id]);
  const row = state?.records?.find(r => r.task === task);
  const active = state?.records?.find(r => ['running', 'paused'].includes(r.status));
  useEffect(() => {
    if (row?.status !== 'running') return;
    const timer = window.setInterval(() => setTick((performance.now()-received)/1000), 1000);
    return () => window.clearInterval(timer);
  }, [row?.status, received]);
  async function send(action: string) {
    setBusy(true); setError('');
    try {
      accept(await request<State>(`/evaluation/tasks/${task}`, { method: 'POST', body: { action, revision: row?.revision ?? 0, needed_help: help } }));
    } catch (e) {
      setError(`${e instanceof Error ? e.message : 'Error de conexión'}. Recuperá el estado antes de seguir; no se asume que se haya guardado.`);
      setUncertain(true);
    } finally { setBusy(false); }
  }
  async function download() {
    setBusy(true); setError('');
    try {
      const r = await fetch(`${API_PREFIX}/evaluation/export`, { headers: { Authorization: `Bearer ${tokenStore.access}` } });
      if (!r.ok) throw new Error('No se pudo exportar. Verificá la sesión e intentá de nuevo.');
      const url = URL.createObjectURL(await r.blob());
      const a = document.createElement('a'); a.href = url; a.download = 'metricas-evaluacion.csv'; a.click();
      window.setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (e) { setError(e instanceof Error ? e.message : 'Error al exportar'); }
    finally { setBusy(false); }
  }
  if (user?.role === 'ADMIN') return <details style={{ margin: '8px 24px' }}>
    <summary>Métricas de la evaluación P01–P05</summary>
    <p>Resultados declarados, tiempos de reloj menos pausas y operaciones auditadas. No equivalen a observación humana.</p>
    <button disabled={busy} onClick={() => void download()}>Descargar métricas CSV</button>
    {error && <p role="alert">{error}</p>}
  </details>;
  if (!candidate || state?.enabled === false) return null;
  const seconds = Math.floor((row?.measured_seconds ?? 0) + (row?.status === 'running' ? tick : 0));
  return <details data-cy="evaluation-control" style={{ margin: '8px 24px', padding: 10, border: '1px solid #bccfc8', borderRadius: 8 }}>
    <summary>Evaluación {state?.participant ?? ''} · {active ? `Tarea ${active.task}: ${statusLabel[active.status]}` : `${state?.records?.length ?? 0}/15 registradas`}</summary>
    <p>Antes de cada tarea pulsá Iniciar; al terminar indicá el resultado real. La guía y sus pasos no cambian. Se guardan tiempos y estados, sin contenido clínico.</p>
    <p>El reloj incluye esperas, desconexión y pestañas cerradas salvo que pulses Pausar. Estos tiempos no miden atención efectiva. La participación debe estar acordada con el equipo investigador.</p>
    {state?.enabled && <>
      <label>Tarea del instrumento <select aria-label="Tarea del instrumento" value={task} disabled={busy || !!active || uncertain} onChange={e => { setTask(Number(e.target.value)); setHelp(false); }}>
        {state.tasks?.map((title, i) => <option value={i+1} key={i}>{i+1}. {title}{state.records?.some(r => r.task === i+1) ? ' — registrada' : ''}</option>)}
      </select></label>
      <p data-cy="evaluation-status">{row ? statusLabel[row.status] : 'Sin iniciar'} · {Math.floor(seconds/60)}:{String(seconds%60).padStart(2,'0')} (aproximado en pantalla; servidor al guardar)</p>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        {!row && <button disabled={busy || uncertain} onClick={() => void send('start')}>Iniciar tarea</button>}
        {row?.status === 'running' && <>
          <button className="secondary" disabled={busy || uncertain} onClick={() => void send('pause')}>Pausar medición</button>
          <label><input type="checkbox" checked={help} onChange={e => setHelp(e.target.checked)} /> Recibí ayuda adicional a la guía o al video</label>
          <button disabled={busy || uncertain} onClick={() => void send('complete')}>Finalizar: la completé</button>
          <button className="secondary" disabled={busy || uncertain} onClick={() => void send('blocked')}>No pude completarla</button>
        </>}
        {row?.status === 'paused' && <button disabled={busy || uncertain} onClick={() => void send('resume')}>Reanudar medición</button>}
        <button className="secondary" disabled={busy} onClick={() => void reload()}>Recuperar estado guardado</button>
      </div>
      {state.records?.length === 15 && !active && <p>Registro finalizado. Completá el Google Forms facilitado por Joel.</p>}
    </>}
    {error && <p role="alert">{error} {!state?.enabled && <button disabled={busy} onClick={() => void reload()}>Recuperar estado guardado</button>}</p>}
    {busy && <p role="status">Guardando o recuperando registro…</p>}
    <small>Si ocurre un problema, indicá al coordinador el número de tarea. No ingreses datos reales.</small>
  </details>;
}
